
import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, TooltipProps } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, TrendingUp } from "lucide-react";

interface MonthlyActivityProps {
  isLoading: boolean;
  data: any[];
}

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border rounded-md shadow-md p-3">
        <p className="font-medium">{label}</p>
        <div className="space-y-1 mt-2">
          <p className="text-sm flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#0088FE]" />
            <span>Rides: </span> 
            <span className="font-medium">{payload[0].value}</span>
          </p>
          <p className="text-sm flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#00C49F]" />
            <span>Distance: </span> 
            <span className="font-medium">{payload[1].value} km</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

const MonthlyActivityChart: React.FC<MonthlyActivityProps> = ({ isLoading, data }) => {
  return (
    <Card className="col-span-2">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" /> 
              Monthly Activity
            </CardTitle>
            <CardDescription>
              Overview of rides and distance traveled per month
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full bg-[#0088FE]" />
              <span>Rides</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full bg-[#00C49F]" />
              <span>Distance (km)</span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="h-[400px]">
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : data.length === 0 ? (
          <div className="flex h-full items-center justify-center flex-col">
            <TrendingUp className="h-12 w-12 text-muted-foreground mb-2" />
            <p className="font-medium text-muted-foreground">No activity data available</p>
            <p className="text-xs text-muted-foreground">Ride data will appear here once recorded</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }}
              barGap={10}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis 
                dataKey="month" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
                dy={10}
              />
              <YAxis 
                yAxisId="left" 
                orientation="left" 
                stroke="#0088FE"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
                width={30}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right" 
                stroke="#00C49F"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
                width={50}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ display: 'none' }} />
              <Bar 
                yAxisId="left" 
                dataKey="rides" 
                name="Rides" 
                fill="#0088FE" 
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
              <Bar 
                yAxisId="right" 
                dataKey="distance" 
                name="Distance (km)" 
                fill="#00C49F" 
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default MonthlyActivityChart;
